import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    TextField,
    Autocomplete,
    Typography,
    Paper,
    Button,
    CircularProgress,
    InputAdornment,
    Chip,
    Avatar,
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
} from '@mui/icons-material';

interface Client {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    gstin?: string;
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
    lastInvoiceDate?: Date;
}

interface SmartClientSelectorProps {
    clients: Client[];
    selectedClient: Client | null;
    onClientSelect: (client: Client | null) => void;
    onAddNewClient: () => void;
    loading?: boolean;
}

const SmartClientSelector: React.FC<SmartClientSelectorProps> = ({
    clients,
    selectedClient,
    onClientSelect,
    onAddNewClient,
    loading = false,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [open, setOpen] = useState(false);

    // Sort clients by recent usage
    const sortedClients = useMemo(() => {
        return [...clients].sort((a, b) => {
            // Recent clients first
            if (a.lastInvoiceDate && b.lastInvoiceDate) {
                return b.lastInvoiceDate.getTime() - a.lastInvoiceDate.getTime();
            }
            if (a.lastInvoiceDate) return -1;
            if (b.lastInvoiceDate) return 1;
            // Then alphabetically
            return a.name.localeCompare(b.name);
        });
    }, [clients]);

    // Fuzzy search across multiple fields
    const filteredClients = useMemo(() => {
        if (!searchQuery) return sortedClients;

        const query = searchQuery.toLowerCase();
        return sortedClients.filter(client => {
            return (
                client.name.toLowerCase().includes(query) ||
                client.email?.toLowerCase().includes(query) ||
                client.phone?.includes(query) ||
                client.gstin?.toLowerCase().includes(query) ||
                client.city?.toLowerCase().includes(query)
            );
        });
    }, [sortedClients, searchQuery]);

    return (
        <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Bill To
            </Typography>

            <Autocomplete
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                options={filteredClients}
                value={selectedClient}
                onChange={(event, newValue) => {
                    onClientSelect(newValue);
                }}
                inputValue={searchQuery}
                onInputChange={(event, newInputValue) => {
                    setSearchQuery(newInputValue);
                }}
                getOptionLabel={(option) => option.name}
                loading={loading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder="Search client by name, email, phone, or GSTIN..."
                        variant="outlined"
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'background.paper',
                            }
                        }}
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', py: 1 }}>
                            <Avatar
                                sx={{
                                    width: 40,
                                    height: 40,
                                    mr: 2,
                                    bgcolor: option.gstin ? 'primary.main' : 'grey.400',
                                }}
                            >
                                {option.gstin ? <BusinessIcon /> : <PersonIcon />}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="body1" fontWeight="medium">
                                    {option.name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                    {option.email && (
                                        <Typography variant="caption" color="text.secondary">
                                            {option.email}
                                        </Typography>
                                    )}
                                    {option.phone && (
                                        <Typography variant="caption" color="text.secondary">
                                            • {option.phone}
                                        </Typography>
                                    )}
                                </Box>
                                {option.gstin && (
                                    <Chip
                                        label={`GSTIN: ${option.gstin}`}
                                        size="small"
                                        sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </li>
                )}
                noOptionsText={
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            No clients found
                        </Typography>
                        <Button
                            startIcon={<AddIcon />}
                            variant="outlined"
                            size="small"
                            onClick={onAddNewClient}
                        >
                            Add New Client
                        </Button>
                    </Box>
                }
                PaperComponent={({ children, ...other }) => (
                    <Paper {...other} elevation={8}>
                        {children}
                        <Box
                            sx={{
                                borderTop: 1,
                                borderColor: 'divider',
                                p: 1,
                            }}
                        >
                            <Button
                                fullWidth
                                startIcon={<AddIcon />}
                                onClick={onAddNewClient}
                                sx={{
                                    justifyContent: 'flex-start',
                                    color: 'primary.main',
                                    textTransform: 'none',
                                }}
                            >
                                Add New Client
                            </Button>
                        </Box>
                    </Paper>
                )}
                sx={{ mb: 2 }}
            />

            {/* Selected Client Details */}
            {selectedClient && (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        backgroundColor: 'primary.50',
                        borderColor: 'primary.200',
                    }}
                >
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                        Client Details
                    </Typography>
                    <Typography variant="body2">
                        <strong>{selectedClient.name}</strong>
                    </Typography>
                    {selectedClient.address && (
                        <Typography variant="body2" color="text.secondary">
                            {selectedClient.address}
                            {selectedClient.city && `, ${selectedClient.city}`}
                            {selectedClient.state && `, ${selectedClient.state}`}
                            {selectedClient.pincode && ` - ${selectedClient.pincode}`}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                        {selectedClient.email && (
                            <Typography variant="caption" color="text.secondary">
                                📧 {selectedClient.email}
                            </Typography>
                        )}
                        {selectedClient.phone && (
                            <Typography variant="caption" color="text.secondary">
                                📱 {selectedClient.phone}
                            </Typography>
                        )}
                        {selectedClient.gstin && (
                            <Typography variant="caption" color="text.secondary">
                                🏢 {selectedClient.gstin}
                            </Typography>
                        )}
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default SmartClientSelector;
